import { Fragment, HTMLAttributes, useState } from "react"

import { CircleX, Copy, CopyCheck, FolderSearch, Mail } from "lucide-react"

import useTranslator from "@hooks/use-translator"

import { DictType } from "@pma/types"
import { Button } from "@components/common/button"
import { Drawer } from "@components/common/drawer"

import CutFile from "./cut-file"
import MoveFile from "./move-file"
import DownloadFile from "./download-file"
import DeleteFile from "../dashboard/delete-file"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import CopyFile from "./copy-file"

interface Props extends HTMLAttributes<HTMLDivElement> {
  tasks?: any
  handleBatchSelection: () => void
  rootFolder: any
  resetPageParam: () => void
  activeFolder: any
  selectAllFiles: () => void | undefined
  showBatchSelection: boolean
  totalFiles: number
  clearSelection: () => void | undefined
  setCutFiles: React.Dispatch<React.SetStateAction<DictType[]>>
  setCopyFiles: React.Dispatch<React.SetStateAction<DictType[]>>
  setFolderItems: React.Dispatch<React.SetStateAction<{ [id: number]: any[] }>>
  setBreadcrumbs: React.Dispatch<React.SetStateAction<{ [id: number]: any[] }>>
  foldersMeta: { [id: number]: number }
  folderItems: { [id: number]: any[] }
  cutFiles: DictType[]
  handleTreeItemFolderClick: (item: DictType, forceExpand: boolean) => void
  setFoldersMeta: React.Dispatch<React.SetStateAction<{ [id: number]: number }>>
  breadcrumbs: { [id: number]: any[] }
  setActiveFolder: React.Dispatch<React.SetStateAction<any>>
  setPreFolderItems: React.Dispatch<React.SetStateAction<{ [id: number]: any[] }>>
  setFolderId: React.Dispatch<React.SetStateAction<number>>
  setSelectedFiles: React.Dispatch<React.SetStateAction<DictType[]>>
  setShareViaEmailModal: React.Dispatch<React.SetStateAction<boolean>>
}

export default function FilesBatchActions({
  tasks = [],
  handleBatchSelection,
  rootFolder,
  resetPageParam,
  activeFolder,
  selectAllFiles,
  showBatchSelection,
  totalFiles,
  clearSelection,
  setCutFiles,
  setCopyFiles,
  setFolderItems,
  setBreadcrumbs,
  foldersMeta,
  folderItems,
  cutFiles,
  handleTreeItemFolderClick,
  setFoldersMeta,
  breadcrumbs,
  setActiveFolder,
  setPreFolderItems,
  setFolderId,
  setShareViaEmailModal,
  setSelectedFiles,
}: Props) {
  const { translate, locale } = useTranslator()
  const [moveFileModal, setMoveFileModal] = useState<boolean>(false)

  const showDeSelectAll = Number(totalFiles) === tasks.length

  return (
    <Fragment>
      <Drawer>
        <div className='sm:w-max-translate-x-1/2 fixed bottom-3 left-3 right-0 z-40 flex w-min flex-row items-center justify-between rounded-md border-2 border-white bg-primary/90 sm:bottom-3 sm:left-0 sm:w-fit sm:p-1 md:left-4 lg:left-[20%]'>
          {showBatchSelection && (
            <>
              <div className='flex flex-row items-center justify-evenly gap-2 p-3'>
                <div className='text-wrap-nowrap flex md:justify-between'>
                  <div className='text-sm font-semibold text-white'>
                    <span className=' '>{`${translate("Selected")} `}</span>
                    <span className=''>{tasks.length}</span>{" "}
                    <span className=' '>{` ${tasks.length === 1 ? translate("item") : translate("items")}`}</span>
                  </div>
                </div>
                <div className='flex flex-row gap-2'>
                  <div>
                    {showDeSelectAll ? (
                      <Button variant={"outline"} size={"sm"} onClick={clearSelection}>
                        <div className='flex gap-1'>
                          <Copy size={14} />
                          <span className='hidden min-w-[30px] text-left text-[12px] sm:block sm:text-[10px]'>
                            {translate("Deselect All")}
                          </span>
                        </div>
                      </Button>
                    ) : (
                      <Button variant={"outline"} size={"sm"} onClick={selectAllFiles}>
                        <div className='flex gap-1'>
                          <CopyCheck size={14} />
                          <span className='hidden min-w-[30px] text-left text-[12px] sm:block sm:text-[10px]'>
                            {translate("Select All")}
                          </span>
                        </div>
                      </Button>
                    )}
                  </div>
                  <div className={`${tasks.length === 0 && "disable"} `}>
                    <DownloadFile files={tasks} />
                  </div>
                  <div className={`${tasks.length === 0 && "disable"} `}>
                    <Tooltip>
                      <TooltipTrigger>
                        <Button
                          variant='outline'
                          className={`flex flex-row gap-2 ${locale === "he" ? "sm:w-[120px] md:w-min" : "sm:w-[110px] md:w-min"} items-center justify-between hover:bg-slate-300`}
                          size={"sm"}
                          onClick={() => setMoveFileModal(!moveFileModal)}
                        >
                          <FolderSearch className='' size={16} />
                          <span className='hidden min-w-[30px] flex-1 text-left text-[12px] sm:block sm:text-[10px]'>
                            {`${translate("Move selected files")}`}
                          </span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className=''>
                        {translate("Move selected files")}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className={`${tasks.length === 0 && "disable"}`}>
                    <CutFile
                      fileArr={tasks}
                      activeFolder={activeFolder}
                      setCutFiles={setCutFiles}
                      folderItems={folderItems}
                      setFolderItems={setFolderItems}
                      setPreFolderItems={setPreFolderItems}
                      setFolderId={setFolderId}
                      handleBatchSelection={handleBatchSelection}
                    />
                  </div>
                  <div className={`${tasks.length === 0 && "disable"}`}>
                    <CopyFile fileArr={tasks} setCopyFiles={setCopyFiles} />
                  </div>
                  <div className={`${tasks.length === 0 && "disable"}`}>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => {
                        setSelectedFiles(tasks)
                        setShareViaEmailModal(true)
                      }}
                      disabled={!tasks || tasks.length === 0}
                      className='flex items-center gap-1'
                    >
                      <Mail size={14} />
                      <span className='hidden min-w-[30px] text-left text-[12px] sm:block sm:text-[10px]'>
                        {translate("Share via Email")}
                      </span>
                    </Button>
                  </div>
                  <div className={`${tasks.length === 0 && "disable"} `}>
                    <DeleteFile
                      showBatchSelection={showBatchSelection}
                      fileArr={tasks.length === 0 && showBatchSelection ? [1] : tasks}
                      handleBatchSelection={handleBatchSelection}
                      setFolderItems={setFolderItems}
                      setBreadcrumbs={setBreadcrumbs}
                      foldersMeta={foldersMeta}
                    />
                  </div>
                </div>

                <div className=''>
                  <Button
                    variant={"destructive"}
                    className='text-black-400 flex-row gap-1 px-2 text-[12px] sm:text-[10px]'
                    onClick={() => handleBatchSelection()}
                  >
                    <CircleX size={14} />
                    <span className='hidden min-w-[30px] flex-1 text-left sm:block'>
                      {translate("Clear selection")}
                    </span>
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </Drawer>

      {moveFileModal && (
        <MoveFile
          fileArr={tasks.length === 0 && showBatchSelection ? [1] : tasks}
          moveFileModal={moveFileModal}
          resetPageParam={resetPageParam}
          setMoveFileModal={setMoveFileModal}
          rootFolder={rootFolder}
          parentFolder={activeFolder}
          setCutFiles={setCutFiles}
          setFolderItems={setFolderItems}
          foldersMeta={foldersMeta}
          folderItems={folderItems}
          cutFiles={cutFiles}
          setFoldersMeta={setFoldersMeta}
          handleBatchSelection={handleBatchSelection}
          handleTreeItemFolderClick={handleTreeItemFolderClick}
          file={undefined}
          setBreadcrumbs={setBreadcrumbs}
          breadcrumbs={breadcrumbs}
          setActiveFolder={setActiveFolder}
        />
      )}
    </Fragment>
  )
}
